import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import {
  getAlert,
  getAlerts,
  createAlert,
  updateAlert,
  type CreateAlertPayload,
  type UpdateAlertPayload,
  deleteAlert,
} from "../lib/alerts-api.js";
import { SENIORITY_OPTIONS, LANGUAGE_OPTIONS } from "../constants/specializations.js";
import { SpecializationPicker } from "../components/SpecializationPicker.js";
import { MultiSelectPills } from "../components/MultiSelectPills.js";
import { TagsInput } from "../components/TagsInput.js";
import { formatUserErrorMessage } from "../lib/error-formatter.js";
import { ROUTES } from "../routes.js";

export function AlertFormScreen(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [specialization, setSpecialization] = useState("");
  const [seniorities, setSeniorities] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>(["ru", "en"]);
  const [skills, setSkills] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch existing alert if editing
  const { data: existingAlert, isLoading: isLoadingExisting } = useQuery({
    queryKey: ["alert", id],
    queryFn: () => getAlert(id!),
    enabled: isEditing,
  });

  const { data: allAlerts = [] } = useQuery({
    queryKey: ["alerts"],
    queryFn: getAlerts,
    enabled: !isEditing,
  });

  useEffect(() => {
    if (existingAlert) {
      setSpecialization(existingAlert.specialization);
      setSeniorities(existingAlert.seniorities);
      setLanguages(
        existingAlert.languages && existingAlert.languages.length > 0
          ? existingAlert.languages
          : ["ru", "en"],
      );
      setSkills(existingAlert.skills);
      setExclusions(existingAlert.exclusions.map((e) => e.value));
    }
  }, [existingAlert]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setErrorMessage(null);
      if (isEditing && id) {
        const payload: UpdateAlertPayload = {
          seniorities,
          languages,
          skills,
          exclusions: exclusions.map((val) => ({ value: val, scope: "everywhere" })),
        };
        return updateAlert(id, payload);
      } else {
        const createPayload: CreateAlertPayload = {
          specialization,
        };
        const created = await createAlert(createPayload);
        const updatePayload: UpdateAlertPayload = {
          seniorities,
          languages,
          skills,
          exclusions: exclusions.map((val) => ({ value: val, scope: "everywhere" })),
        };
        try {
          return await updateAlert(created.id, updatePayload);
        } catch (error) {
          // If update fails immediately after creation, delete the partially created alert
          await deleteAlert(created.id).catch(() => {});
          throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      navigate(ROUTES.MAIN);
    },
    onError: (err) => {
      setErrorMessage(formatUserErrorMessage(err, "Не удалось сохранить профиль"));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!specialization.trim()) {
      setErrorMessage("Пожалуйста, выберите специализацию.");
      return;
    }

    if (!languages || languages.length === 0) {
      setErrorMessage("Пожалуйста, выберите язык вакансий.");
      return;
    }

    if (!isEditing) {
      const alreadyExists = allAlerts.some((a) => a.specialization === specialization);
      if (alreadyExists) {
        setErrorMessage("У вас уже есть профиль для этой специализации.");
        return;
      }
    }

    saveMutation.mutate();
  };

  if (isEditing && isLoadingExisting) {
    return (
      <div data-testid="alert-form-screen">
        <div
          data-testid="alert-form-loading"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              border: "3px solid var(--color-frost-border)",
              borderTopColor: "var(--color-muted-gold)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="alert-form-screen">
      <form
        data-testid="alert-form"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "20px", paddingBottom: "32px" }}
      >
      {/* Header */}
      <div
        style={{
          height: "56px",
          display: "flex",
          alignItems: "center",
          padding: "4px 4px",
          flexShrink: 0,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "22px",
            lineHeight: "26px",
            fontWeight: 700,
            color: "var(--color-graphite)",
            letterSpacing: "-0.02em",
          }}
        >
          {isEditing ? "Настройка профиля" : "Новый профиль"}
        </h1>
      </div>

      {/* Error Banner */}
      {errorMessage && (
        <div
          data-testid="alert-form-error"
          style={{
            padding: "12px 16px",
            backgroundColor: "var(--color-destructive-bg)",
            color: "var(--color-destructive)",
            borderRadius: "var(--radius-input)",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <AlertCircle size={18} />
          {errorMessage}
        </div>
      )}

      {/* Section 1: Specialization */}
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <label style={{ fontSize: "15px", fontWeight: 600, color: "var(--color-graphite)" }}>
          Специализация <span style={{ color: "var(--color-destructive)" }}>*</span>
        </label>
        <SpecializationPicker
          value={specialization}
          onChange={(val) => {
            setSpecialization(val);
            setErrorMessage(null);
          }}
          disabled={isEditing}
          disabledOptions={isEditing ? [] : allAlerts.map(a => a.specialization)}
        />
      </div>

      {/* Section 2: Grade */}
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div>
          <label
            style={{
              fontSize: "15px",
              fontWeight: 600,
              color: "var(--color-graphite)",
              display: "block",
              marginBottom: "10px",
            }}
          >
            Грейд
          </label>
          <MultiSelectPills
            options={SENIORITY_OPTIONS}
            selectedValues={seniorities}
            onChange={setSeniorities}
          />
        </div>
      </div>

      {/* Section 3: Language */}
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <label style={{ fontSize: "15px", fontWeight: 600, color: "var(--color-graphite)" }}>
          Язык вакансий
        </label>
        <MultiSelectPills
          options={LANGUAGE_OPTIONS}
          selectedValues={
            languages.includes("ru") && languages.includes("en")
              ? ["any"]
              : languages.includes("ru")
              ? ["ru"]
              : languages.includes("en")
              ? ["en"]
              : ["any"]
          }
          onChange={(newVals) => {
            if (newVals.length === 0) return;
            const last = newVals[newVals.length - 1];
            if (last === "any") {
              setLanguages(["ru", "en"]);
            } else if (last === "ru") {
              setLanguages(["ru"]);
            } else if (last === "en") {
              setLanguages(["en"]);
            }
          }}
        />
      </div>

      {/* Section 4: Skills (+ Tags) */}
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div>
          <label style={{ fontSize: "15px", fontWeight: 600, color: "var(--color-graphite)" }}>
            Ключевые навыки
          </label>
          <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "var(--color-slate)" }}>
            Будем присылать вакансии, в которых есть хотя бы один из указанных навыков
          </p>
        </div>
        <TagsInput
          tags={skills}
          onChange={setSkills}
          placeholder="Например: React, TypeScript, GraphQL"
          variant="skills"
        />
      </div>

      {/* Section 5: Exclusions (— Tags) */}
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div>
          <label style={{ fontSize: "15px", fontWeight: 600, color: "var(--color-graphite)" }}>
            Исключения
          </label>
          <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "var(--color-slate)" }}>
            Не будем присылать вакансии с указанными словами
          </p>
        </div>
        <TagsInput
          tags={exclusions}
          onChange={setExclusions}
          placeholder="Например: Gambling, Legacy, 1C"
          variant="exclusions"
        />
      </div>

      {/* Save Action Button */}
      <button
        type="submit"
        data-testid="save-alert-btn"
        disabled={saveMutation.isPending || !specialization.trim()}
        style={{
          width: "100%",
          padding: "14px",
          backgroundColor: !specialization.trim()
            ? "var(--color-canvas)"
            : "var(--color-muted-gold)",
          color: !specialization.trim() ? "var(--color-slate)" : "var(--color-paper)",
          borderRadius: "var(--radius-button)",
          border: "none",
          fontSize: "14px",
          fontWeight: 600,
          cursor: saveMutation.isPending || !specialization.trim() ? "not-allowed" : "pointer",
          boxShadow: "var(--shadow-floating)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          marginTop: "8px",
          boxSizing: "border-box",
        }}
      >
        {saveMutation.isPending ? "Сохранение..." : "Сохранить"}
      </button>
    </form>
    </div>
  );
}

export default AlertFormScreen;
